import PropTypes from 'prop-types';
import React from 'react';
import { UX2, UX, constants } from '@wsb/guac-widget-core';
import { getMobileViewportHeight } from '../../../../../utils/elements';
import dataAids from '../../constants/dataAids';
import { SEARCH_PARAMS } from '../../constants/navigation';
import { ICON_SIZE, MIN_TARGET } from '../../../../../constants';
import { DESKTOP_NAV, DESKTOP_NAV_COVER, NAV_DRAWER, SIDEBAR } from '../../constants/searchFormLocations';
import { noop } from 'lodash';
import SearchFormInput from './SearchFormInput';
import MagnifyIcon from './MagnifyIcon';
import AbsLink from '../../AbsLink';
import PortalContainer from '../../PortalContainer';
import SidebarSearchContainer from './SidebarSearchContainer';
import { isDeviceMobile } from '../../utils/viewDevice';

const alignCenter = {
  display: 'flex',
  alignItems: 'center'
};

class SearchForm extends React.Component {
  constructor(props, { viewDevice }) {
    super(props);

    const { searchFormLocation } = this.props;
    this.inNavigationDrawer = searchFormLocation === NAV_DRAWER;
    this.state = {
      isMobile: isDeviceMobile(viewDevice)
    };
  }

  handleMatchMedia = (data) => {
    const { viewDevice, renderMode } = this.context;
    const isMobile = renderMode === constants.renderModes.PUBLISH
      ? data.size === 'xs' : isDeviceMobile(viewDevice);
    if (isMobile !== this.state.isMobile) {
      this.setState({ isMobile });
    }
  }

  renderMagnifyIcon = () => {
    const {
      showSearch,
      section,
      iconSection,
      searchOpen,
      handleFormSubmit,
      iconStyles
    } = this.props;

    return (
      <MagnifyIcon
        showSearch={ showSearch }
        section={ section }
        iconSection={ iconSection }
        onSearchOpenClick={ searchOpen }
        onSearchClosedClick={ handleFormSubmit }
        inNavigationDrawer={ this.inNavigationDrawer }
        iconStyles={ iconStyles }
      />
    );
  };

  renderForm = () => {
    const {
      showSearch,
      staticContent,
      uniqueId,
      searchFormLocation,
      searchInputOnChange,
      searchQuery,
      handleFormSubmit
    } = this.props;
    const { isMobile } = this.state;

    return (
      <SearchFormInput
        showSearch={ showSearch }
        staticContent={ staticContent }
        uniqueId={ uniqueId }
        onChange={ searchInputOnChange }
        searchQuery={ searchQuery }
        formSubmit={ handleFormSubmit }
        isMobile={ isMobile }
        searchFormLocation={ searchFormLocation }
      />
    );
  };

  renderCloseIcon = (isMobile) => {
    const { showSearch, keepOpen, searchClose } = this.props;
    const styles = {
      display: showSearch && !keepOpen ? 'block' : 'none',
      position: isMobile ? 'fixed' : 'static',
      cursor: 'pointer',
      right: 'small',
      top: 'small',
      marginLeft: isMobile ? 0 : 'medium'
    };

    return (
      <UX2.Element.CloseIcon
        onClick={ searchClose }
        style={ styles }
        data-aid={ dataAids.SEARCH_CLOSE_RENDERED }
        size={ ICON_SIZE }
        minTarget={ true }
      />
    );
  };

  generatePositionValues() {
    const { showSearch, iconSizeMobile, isIE11, ieSearchPos } = this.props;
    const { isMobile } = this.state;
    const { renderMode } = this.context;
    const isPreviewAndIE11 = renderMode === constants.renderModes.PREVIEW && isIE11;
    let topVal, leftVal, widthVal, heightVal;
    if (!showSearch) {
      topVal = '50%';
      leftVal = 'auto';
      widthVal = iconSizeMobile;
      heightVal = 'auto';
    } else if (isPreviewAndIE11 && showSearch && ieSearchPos && isMobile) {
      leftVal = ieSearchPos[0];
      topVal = ieSearchPos[1];
      widthVal = ieSearchPos[2];
      heightVal = ieSearchPos[3];
    } else {
      topVal = '0px';
      leftVal = '0px';
      widthVal = '100%';
      heightVal = '100%';
    }
    return { topVal, leftVal, widthVal, heightVal };
  }

  renderMobileSearchContainer() {
    const { showSearch, uniqueId, searchCategoryOverrides } = this.props;
    const { renderMode } = this.context;
    const { topVal, leftVal, widthVal, heightVal } = this.generatePositionValues();
    const containerStyles = {
      display: 'flex',
      flexDirection: 'column',
      position: showSearch ? 'fixed' : 'absolute',
      top: topVal,
      left: leftVal,
      right: showSearch ? 'auto' : '0px',
      width: widthVal,
      height: showSearch ? getMobileViewportHeight(renderMode) : heightVal,
      transform: showSearch ? 'none' : 'translateY(-50%)',
      maxWidth: showSearch ? '100%' : widthVal
    };
    if (!showSearch) {
      containerStyles.backgroundColor = 'transparent';
    }
    const ContainerElement = showSearch ? UX2.Group.NavigationDrawer : UX2.Element.Block;
    let category;

    if (searchCategoryOverrides) {
      if (showSearch) {
        category = searchCategoryOverrides.openCategory;
      } else {
        category = searchCategoryOverrides.closeCategory;
      }
    }

    return (
      <ContainerElement
        id={ `${uniqueId}-container` }
        style={ containerStyles }
        { ...searchCategoryOverrides && { category } }
      >
        {this.renderCloseIcon(true)}
        <UX2.Element.Block style={{ padding: showSearch ? 'large' : 'none' }}>
          <UX2.Element.Block style={{ position: 'relative' }}>
            {this.renderForm()}
            {this.renderMagnifyIcon()}
          </UX2.Element.Block>
        </UX2.Element.Block>
      </ContainerElement>
    );
  }

  // eslint-disable-next-line id-length
  renderNavigationDrawerSearchContainer() {
    const { showSearch, iconSizeDesktop } = this.props;
    const styles = {
      container: {
        ...alignCenter,
        paddingVertical: 'xsmall',
        width: showSearch ? '100%' : iconSizeDesktop
      }
    };

    return (
      <UX2.Element.Block style={ styles.container }>
        { this.renderForm() }
        { this.renderMagnifyIcon() }
      </UX2.Element.Block>
    );
  }

  renderSidebarSearchContainer() {
    const { showSearch, iconSizeDesktop, sidebarWidth, uniqueId } = this.props;

    return (
      <SidebarSearchContainer
        iconSizeDesktop={ iconSizeDesktop }
        showSearch={ showSearch }
        uniqueId={ uniqueId }
        sidebarWidth={ sidebarWidth }
        renderForm={ this.renderForm }
        renderMagnifyIcon={ this.renderMagnifyIcon }
        renderCloseIcon={ this.renderCloseIcon }
      />
    );
  }

  renderDesktopSearchContainer() {
    const { searchPosition, showSearch, iconSizeDesktop, searchWidth, showBackground, searchBgStyle, searchCategoryOverrides } = this.props;
    const showSectionBackground = searchPosition === 'centered' || showBackground;
    const styles = {
      container: {
        ...alignCenter,
        position: 'absolute',
        zIndex: showSearch ? '1' : '0',
        right: '0px',
        top: '50%',
        paddingVertical: showSearch && !showSectionBackground ? 'small' : 'none',
        backgroundColor: showSearch && showSectionBackground ? 'section' : 'transparent',
        width: showSearch ? searchWidth : iconSizeDesktop,
        transform: showSearch && searchPosition === 'centered'
          ? 'translate(60%, -50%)'
          : 'translateY(-50%)',
        ...showSearch && searchBgStyle
      },
      searchFormWrapper: {
        width: showSearch ? '100%' : iconSizeDesktop
      }
    };
    let category;

    if (searchCategoryOverrides) {
      if (showSearch) {
        category = searchCategoryOverrides.openCategory;
      } else {
        category = searchCategoryOverrides.closeCategory;
      }
    }

    return (
      <UX2.Element.Block style={ styles.container }>
        <UX2.Element.Block
          style={ styles.searchFormWrapper }
          { ...searchCategoryOverrides && { category } }
        >
          { this.renderForm() }
          { this.renderMagnifyIcon() }
        </UX2.Element.Block>
        { this.renderCloseIcon() }
      </UX2.Element.Block>
    );
  }

  renderCoverSearchContainer() {
    const {
      searchPosition,
      showSearch,
      iconSizeDesktop,
      searchWidth,
      navWidth,
      searchCategoryOverrides,
      formContainerId,
      onHomepage,
      hasNavBackground,
      section
    } = this.props;

    const styles = {
      formWrapper: {
        display: showSearch ? 'block' : 'none',
        zIndex: 1,
        position: 'absolute',
        right: '0px',
        top: '50%',
        width: searchWidth,
        maxWidth: navWidth ? `${navWidth / 2}px` : '600px',
        transform: searchPosition === 'centered' ? 'translate(60%, -50%)' : 'translateY(-50%)'
      },
      navIcon: {
        width: iconSizeDesktop
      }
    };

    const colorProps = { section };
    if (!hasNavBackground) {
      if (onHomepage) {
        colorProps.category = 'neutral';
      } else {
        colorProps.section = section === 'alt' ? 'default' : 'alt';
      }
    }
    if (searchCategoryOverrides) {
      colorProps.category = searchCategoryOverrides[showSearch ? 'openCategory' : 'closeCategory'];
    }

    const coverSearchForm = (
      <PortalContainer containerId={ formContainerId }>
        <UX2.Element.Block style={ styles.formWrapper }>
          <UX2.Group.SearchPopout { ...colorProps } hasBorder={ hasNavBackground }>
            { this.renderMagnifyIcon() }
            { this.renderForm() }
            { this.renderCloseIcon() }
          </UX2.Group.SearchPopout>
        </UX2.Element.Block>
      </PortalContainer>
    );

    return (
      <UX2.Element.Block>
        <UX2.Element.Block style={ styles.navIcon }>
          { this.renderMagnifyIcon() }
        </UX2.Element.Block>
        { coverSearchForm }
      </UX2.Element.Block>
    );
  }

  renderFormSubmitLink() {
    const {
      searchQuery,
      shopRoute,
      shopPageId,
      domainName,
      pageRoute,
      anchorId,
      useBestMatch
    } = this.props;
    const { renderMode } = this.context;
    const bestMatchQuery = useBestMatch ? '&sortOption=descend_by_match' : '';
    const queryParams = `${SEARCH_PARAMS}&keywords=${searchQuery}${bestMatchQuery}`;

    return (
      <AbsLink
        id={ anchorId }
        href={ `${shopRoute}?${queryParams}` }
        data-page={ shopPageId }
        data-page-query={ queryParams }
        style={{ display: 'none' }}
        renderMode={ renderMode }
        domainName={ domainName }
        pageRoute={ pageRoute }
      />
    );
  }

  renderCloseDropdownLink() {
    const { closeId } = this.props;

    return (
      <UX2.Element.Link
        id={ closeId }
        style={{ display: 'none' }}
        data-close
      />
    );
  }

  render() {
    const {
      iconSizeMobile,
      iconSizeDesktop,
      isShopPage,
      searchFormLocation
    } = this.props;

    let SearchContainer;
    switch (searchFormLocation) {
      case NAV_DRAWER:
        SearchContainer = this.renderNavigationDrawerSearchContainer();
        break;
      case SIDEBAR:
        SearchContainer = this.renderSidebarSearchContainer();
        break;
      case DESKTOP_NAV:
        SearchContainer = this.renderDesktopSearchContainer();
        break;
      case DESKTOP_NAV_COVER:
        SearchContainer = this.renderCoverSearchContainer();
        break;
      default:
        SearchContainer = this.renderMobileSearchContainer();
    }

    return (
      <UX2.Element.Block
        data-aid={ dataAids.SEARCH_FORM_RENDERED }
        style={{
          'width': searchFormLocation === NAV_DRAWER ? '100%' : iconSizeMobile,
          ...alignCenter,
          'position': 'relative',
          '@md': {
            width: iconSizeDesktop
          }
        }}
      >
        <UX.MatchMedia onChange={ this.handleMatchMedia } />
        { SearchContainer }
        { isShopPage ? null : this.renderFormSubmitLink() }
        { this.inNavigationDrawer && this.renderCloseDropdownLink() }
      </UX2.Element.Block>
    );
  }
}

SearchForm.propTypes = {
  isShopPage: PropTypes.bool,
  shopPageId: PropTypes.string,
  shopRoute: PropTypes.string,
  searchPosition: PropTypes.string,
  showBackground: PropTypes.bool,
  isMobile: PropTypes.bool,
  keepOpen: PropTypes.bool,
  iconSizeMobile: PropTypes.string,
  iconSizeDesktop: PropTypes.string,
  domainName: PropTypes.string,
  pageRoute: PropTypes.string,
  ieSearchPos: PropTypes.arrayOf(PropTypes.string),
  isIE11: PropTypes.bool,
  showSearch: PropTypes.bool,
  navWidth: PropTypes.number,
  screenWidth: PropTypes.number,
  searchWidth: PropTypes.string,
  searchQuery: PropTypes.string,
  uniqueId: PropTypes.string,
  searchClose: PropTypes.func,
  staticContent: PropTypes.object,
  handleFormSubmit: PropTypes.func,
  searchInputOnChange: PropTypes.func,
  iconSection: PropTypes.string,
  color: PropTypes.string,
  section: PropTypes.string,
  searchOpen: PropTypes.func,
  anchorId: PropTypes.string,
  closeId: PropTypes.string,
  searchFormLocation: PropTypes.oneOf([DESKTOP_NAV, DESKTOP_NAV_COVER, NAV_DRAWER, SIDEBAR]),
  sidebarWidth: PropTypes.number,
  searchBgStyle: PropTypes.func,
  searchCategory: PropTypes.string,
  searchCategoryOverrides: PropTypes.object,
  iconStyles: PropTypes.object,
  formContainerId: PropTypes.string,
  onHomepage: PropTypes.bool,
  hasNavBackground: PropTypes.bool,
  useBestMatch: PropTypes.bool
};

SearchForm.contextTypes = {
  renderMode: PropTypes.string.isRequired,
  viewDevice: PropTypes.string.isRequired
};

SearchForm.defaultProps = {
  searchPosition: 'default',
  iconSizeMobile: MIN_TARGET,
  iconSizeDesktop: MIN_TARGET,
  ieSearchPos: [],
  searchWidth: '200px',
  searchInputOnChange: noop,
  searchClose: noop,
  searchBackgroundStyle: {},
  iconStyles: {},
  shopRoute: '',
  useBestMatch: false
};

export default SearchForm;
